import { TrashIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow, format } from 'date-fns';
import { useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import { useTranslation } from 'react-i18next';

import { CalendarEvent } from '../stores/useCalendarStore';

import { useDateLocale } from '@/common/hooks/useDateLocale';

interface EventCardProps {
  event: CalendarEvent;
  onDelete: (id: string) => void;
}

export const EventCard = ({ event, onDelete }: EventCardProps) => {
  const { t } = useTranslation();
  const { locale } = useDateLocale();
  const [isFlipped, setIsFlipped] = useState(false);

  const handleFlip = () => setIsFlipped(!isFlipped);

  return (
    <div className="h-[120px]">
      <ReactCardFlip flipDirection="horizontal" isFlipped={isFlipped}>
        {/* Front of card */}
        <div className="cursor-pointer" onClick={handleFlip}>
          <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 h-[120px]">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 rounded-lg p-2 w-12 h-12 flex items-center justify-center">
                <span className="text-blue-600 font-medium">
                  {format(event.event_date, 'd')}
                </span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 truncate">{event.title}</h4>
                <p className="text-sm text-gray-500">
                  {format(event.event_date, 'MMMM d, yyyy', { locale })}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <button
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(event.id);
                }}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
              <div className="px-2 py-0.5 bg-blue-50 rounded-full">
                <span className="text-xs font-medium text-blue-600">
                  {formatDistanceToNow(event.event_date, { addSuffix: false, locale })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Back of card */}
        <div className="cursor-pointer" onClick={handleFlip}>
          <div className="flex flex-col p-4 bg-white rounded-xl border border-gray-200 h-[120px]">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900">{event.title}</h4>
              <button
                className="text-blue-500 hover:text-blue-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleFlip();
                }}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-600 text-sm leading-relaxed">
                  {event.description || t('calendarApp.noDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </ReactCardFlip>
    </div>
  );
};
